import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, ordersAPI } from '../api';
import MediaManager from './MediaManager';
import AdminSoundSettings from './AdminSoundSettings';
import AdminPayments from './AdminPayments';
import AdminReferrals from './AdminReferrals';
import AdminRecommendations from './AdminRecommendations';
import AdminQC from './AdminQC';
import AdminErrors from './AdminErrors';
import AdminBackup from './AdminBackup';
import useWebSocket from '../hooks/useWebSocket';
import { playNotificationSound, loadSettings } from './SoundSystem';
import {
  Users, Package, IndianRupee, Scissors, ShoppingBag,
  Search, RefreshCw, Trash2, LogOut,
  CheckCircle, Ban, Star, ShieldAlert,
} from 'lucide-react';

const PRIVILEGED_ROLES = ['admin', 'founder'];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tailors, setTailors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orderFilter, setOrderFilter] = useState('');

  const isPrivileged = !!user && PRIVILEGED_ROLES.includes(user.role);

  // ─── Unauthorized guard: redirect to homepage after brief notice ───
  useEffect(() => {
    if (authLoading) return;
    if (!isPrivileged) {
      const t = setTimeout(() => navigate('/landing', { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [authLoading, isPrivileged, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, t, o] = await Promise.all([
        adminAPI.stats(), adminAPI.customers(), adminAPI.tailors(), ordersAPI.all()
      ]);
      setStats(s); setCustomers(c); setTailors(t); setOrders(o);
    } catch (err) {
      // If backend rejects (401/403), bounce to homepage
      if (err && (err.status === 401 || err.status === 403)) {
        navigate('/landing', { replace: true });
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (isPrivileged) loadData();
    else setLoading(false);
  }, [isPrivileged, loadData]);

  // Real-time WebSocket — sound + auto-refresh on new orders / updates
  useWebSocket(useCallback((msg) => {
    if (!isPrivileged || !msg || !msg.type) return;
    const settings = loadSettings();
    if (msg.type === 'new_order') {
      if (settings.soundEnabled) playNotificationSound('newOrder');
      loadData();
    } else if (msg.type === 'cancel_order') {
      if (settings.soundEnabled) playNotificationSound('overdue');
      loadData();
    } else if (msg.type === 'order_update') {
      loadData();
    }
  }, [isPrivileged, loadData]));

  const handleDelete = async (id) => {
    try { await adminAPI.deleteUser(id); loadData(); } catch {/* silent */}
  };

  const handleUpdateOrder = async (orderId, status) => {
    try { await ordersAPI.update(orderId, { status }); loadData(); } catch {/* silent */}
  };

  const filteredOrders = orders.filter(o => {
    if (orderFilter && o.status !== orderFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (o.order_id || '').toLowerCase().includes(s)
        || (o.customer_name || '').toLowerCase().includes(s)
        || (o.product || '').toLowerCase().includes(s);
    }
    return true;
  });

  const filteredCustomers = customers.filter(c => !search
    || (c.name || '').toLowerCase().includes(search.toLowerCase())
    || (c.email || '').toLowerCase().includes(search.toLowerCase()));
  const filteredTailors = tailors.filter(t => !search || (t.name || '').toLowerCase().includes(search.toLowerCase()));

  const getStatusBadge = (s) => {
    const m = { placed: '#D4AF37', assigned: '#D4AF37', stitching: '#E5B2B9', qc_pending: '#D4AF37', qc_approved: '#166534', ready: '#166534', completed: '#166534', delivered: '#166534', cancelled: '#991B1B', 'counter-offered': '#D4AF37' };
    return m[s] || '#595959';
  };

  // ─── Loading state while auth is resolving ───
  if (authLoading || (isPrivileged && loading)) {
    return (
      <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // ─── Unauthorized screen ───
  if (!isPrivileged) {
    return (
      <div data-testid="admin-unauthorized" className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
        <div className="w-14 h-14 flex items-center justify-center mb-5" style={{ backgroundColor: '#991B1B' }}>
          <ShieldAlert className="w-7 h-7 text-white" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Unauthorized</h1>
        <p className="text-sm text-center max-w-sm" style={{ color: '#595959' }}>
          You do not have permission to access this page. Redirecting you to the homepage...
        </p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const navTabs = [
    { id: 'overview', label: 'Overview' }, { id: 'orders', label: `Orders (${orders.length})` },
    { id: 'customers', label: `Customers (${stats.customers})` }, { id: 'tailors', label: `Tailors (${stats.tailors})` },
    { id: 'qc', label: 'QC' },
    { id: 'errors', label: 'Error Center' },
    ...(user.role === 'founder' ? [{ id: 'backup', label: 'Backup' }] : []),
    { id: 'payments', label: 'Payments' },
    { id: 'referrals', label: 'Referrals' },
    { id: 'insights', label: 'Insights' },
    { id: 'media', label: 'Media' }, { id: 'sounds', label: 'Sounds' },
  ];

  return (
    <div data-testid="admin-panel" className="min-h-screen" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
      <div className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Admin</h1>
            <span data-testid="admin-role-badge" className="text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5"
              style={{ backgroundColor: user.role === 'founder' ? '#0F0F0F' : '#D4AF37', color: '#FFF' }}>
              {user.role}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} data-testid="admin-refresh" className="p-1.5"><RefreshCw className="w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} /></button>
            <button data-testid="admin-logout" onClick={async () => { await logout(); navigate('/landing'); }} className="p-1.5"><LogOut className="w-4 h-4" style={{ color: '#991B1B' }} strokeWidth={1.5} /></button>
          </div>
        </div>
        <div className="flex overflow-x-auto px-4 gap-1 pb-2 scrollbar-hide">
          {navTabs.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setSearch(''); setOrderFilter(''); }}
              className="flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.05em] uppercase whitespace-nowrap"
              style={{ backgroundColor: tab === t.id ? '#0F0F0F' : '#FFF', color: tab === t.id ? '#FFF' : '#595959', border: `1px solid ${tab === t.id ? '#0F0F0F' : '#E5E5E5'}` }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Users, label: 'Customers', value: stats.customers, color: '#D4AF37' },
                { icon: Scissors, label: 'Tailors', value: stats.tailors, color: '#E5B2B9' },
                { icon: Package, label: 'Total Orders', value: stats.total_orders, color: '#0F0F0F' },
                { icon: ShoppingBag, label: 'Active', value: stats.active_orders, color: '#D4AF37' },
                { icon: CheckCircle, label: 'Completed', value: stats.completed_orders, color: '#166534' },
                { icon: Ban, label: 'Cancelled', value: stats.cancelled_orders, color: '#991B1B' },
              ].map((s, i) => (
                <div key={i} className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                  <s.icon className="w-5 h-5 mb-1.5" style={{ color: s.color }} strokeWidth={1.5} />
                  <p className="text-xl" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{s.value}</p>
                  <p className="text-[10px]" style={{ color: '#595959' }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>Total Revenue</p>
              </div>
              <p className="text-3xl" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{'\u20B9'}{(stats.total_revenue || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
        )}

        {/* Orders */}
        {tab === 'orders' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
                <input data-testid="order-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders..."
                  className="w-full pl-9 pr-3 py-2 text-[12px] bg-white border focus:outline-none" style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
              </div>
              <select data-testid="order-filter" value={orderFilter} onChange={e => setOrderFilter(e.target.value)}
                className="px-3 py-2 text-[11px] bg-white border appearance-none" style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }}>
                <option value="">All</option>
                {['placed', 'assigned', 'stitching', 'ready', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {filteredOrders.map(o => (
              <div key={o.id} className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{o.order_id}</p>
                      <span className="px-1.5 py-px text-[9px] uppercase" style={{ backgroundColor: getStatusBadge(o.status) + '18', color: getStatusBadge(o.status) }}>{o.status}</span>
                    </div>
                    <p className="text-[11px]" style={{ color: '#595959' }}>{o.customer_name} {'\u2022'} {o.product}</p>
                  </div>
                  <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{'\u20B9'}{(o.total_price || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="flex items-center gap-2 text-[10px] mb-2" style={{ color: '#595959' }}>
                  <span>{o.delivery_type}</span><span>{'\u2022'}</span>
                  <span>{o.delivery_date ? new Date(o.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'TBD'}</span>
                  {o.tailor_name && <><span>{'\u2022'}</span><span>{o.tailor_name}</span></>}
                </div>
                {!['completed', 'delivered', 'cancelled'].includes(o.status) && (
                  <div className="flex gap-1.5">
                    {o.status !== 'completed' && <button onClick={() => handleUpdateOrder(o.order_id, 'completed')} className="px-3 py-1.5 text-[10px] bg-[#0F0F0F] text-white">Complete</button>}
                    <button onClick={() => handleUpdateOrder(o.order_id, 'cancelled')} className="px-3 py-1.5 text-[10px] border" style={{ borderColor: '#E5E5E5', color: '#991B1B' }}>Cancel</button>
                  </div>
                )}
              </div>
            ))}
            {filteredOrders.length === 0 && <p className="text-center text-[12px] py-8" style={{ color: '#595959' }}>No orders found</p>}
          </div>
        )}

        {/* Customers */}
        {tab === 'customers' && (
          <div className="space-y-2">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..."
                className="w-full pl-9 pr-3 py-2 text-[12px] bg-white border focus:outline-none" style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
            </div>
            {filteredCustomers.map(c => (
              <div key={c.id} className="bg-white border p-3 flex items-center justify-between" style={{ borderColor: '#E5E5E5' }}>
                <div>
                  <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{c.name}</p>
                  <p className="text-[11px]" style={{ color: '#595959' }}>{c.email}</p>
                  {c.phone && <p className="text-[10px]" style={{ color: '#595959' }}>{c.phone}</p>}
                </div>
                <button onClick={() => handleDelete(c.id)} className="p-2"><Trash2 className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Tailors */}
        {tab === 'tailors' && (
          <div className="space-y-2">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tailors..."
                className="w-full pl-9 pr-3 py-2 text-[12px] bg-white border focus:outline-none" style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
            </div>
            {filteredTailors.map(t => (
              <div key={t.id} className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{t.name}</p>
                    <p className="text-[11px]" style={{ color: '#595959' }}>{t.city || ''} {'\u2022'} {t.speciality || 'General'}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Star className="w-3 h-3 fill-[#D4AF37]" style={{ color: '#D4AF37' }} />
                      <span className="text-[11px]" style={{ color: '#0F0F0F' }}>{t.rating || 0}</span>
                      <span className="text-[10px]" style={{ color: '#595959' }}>{t.total_orders || 0} orders</span>
                      <span className="text-[10px]" style={{ color: t.is_available ? '#166534' : '#991B1B' }}>{t.is_available ? 'Online' : 'Offline'}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(t.id)} className="p-2"><Trash2 className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Media Manager */}
        {tab === 'media' && <MediaManager />}

        {/* Quality Control */}
        {tab === 'qc' && <AdminQC />}

        {/* Error Monitoring Center */}
        {tab === 'errors' && <AdminErrors />}

        {/* Backup Center — founder only */}
        {tab === 'backup' && user.role === 'founder' && <AdminBackup />}

        {/* Payments */}
        {tab === 'payments' && <AdminPayments />}

        {/* Referrals */}
        {tab === 'referrals' && <AdminReferrals />}

        {/* Insights */}
        {tab === 'insights' && <AdminRecommendations />}

        {/* Sound Settings */}
        {tab === 'sounds' && <AdminSoundSettings />}
      </div>

      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none} .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
};

export default AdminPanel;
